import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaCheck,
  FaLayerGroup,
  FaBolt,
  FaGem,
  FaStore,
  FaInfoCircle,
  FaUserPlus,
  FaStoreAlt
} from 'react-icons/fa';
import { PLAN_IDS, planLabel } from '../../utils/planDetails';
import { PLAN_COMMERCIAL_UX_ES } from '../../utils/planCommercialUx';
import PlanesComparisonTable from './PlanesComparisonTable';

const tierStyle = {
  basic: {
    pill: 'from-sky-600 to-cyan-700',
    ring: 'ring-sky-400/30',
    dot: 'bg-sky-400',
    icon: FaStore
  },
  intermediate: {
    pill: 'from-violet-600 to-indigo-700',
    ring: 'ring-violet-400/30',
    dot: 'bg-violet-400',
    icon: FaBolt
  },
  premium: {
    pill: 'from-amber-500 to-orange-600',
    ring: 'ring-amber-400/40',
    dot: 'bg-amber-400',
    icon: FaGem
  }
};

/**
 * Guía comercial START / PRO / EMPRESA — beneficios para el comerciante (tema 0E3).
 */
export default function PlanesAbonoExplainerModal({ open, onClose, initialPlan = 'intermediate' }) {
  const [tab, setTab] = useState(initialPlan);

  useEffect(() => {
    if (open) setTab(initialPlan);
  }, [open, initialPlan]);

  if (!open) return null;

  const copy = PLAN_COMMERCIAL_UX_ES[tab];
  const styles = tierStyle[tab];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="planes-abono-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Cerrar"
      />
      <div className="nexo-modal-shell relative flex max-h-[min(92vh,900px)] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-e0e3-surface shadow-elevated sm:rounded-3xl">
        {/* Header */}
        <div
          className={`relative shrink-0 bg-gradient-to-br px-5 pb-8 pt-6 text-white sm:px-8 sm:pb-10 sm:pt-8 ${styles.pill}`}
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-2 ring-white/30">
                <FaLayerGroup className="text-xl" aria-hidden />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-white/80">Planes 0E3 POS</p>
                <h2 id="planes-abono-title" className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
                  Elegí el plan para tu negocio
                </h2>
                <p className="mt-2 max-w-xl text-sm text-white/90 sm:text-base">
                  Sin tecnicismos: usuarios, sucursales y lo que podés hacer en el día a día de tu comercio.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-white/10 p-2.5 text-white ring-1 ring-white/25 transition hover:bg-white/20"
              aria-label="Cerrar"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="relative mt-6 flex gap-2 overflow-x-auto pb-1 sm:mt-8">
            {PLAN_IDS.map((id) => {
              const active = tab === id;
              const Icon = tierStyle[id].icon;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={[
                    'flex min-w-[8.5rem] shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition',
                    active
                      ? 'bg-white text-slate-900 shadow-lg'
                      : 'bg-white/15 text-white/90 ring-1 ring-white/20 hover:bg-white/25'
                  ].join(' ')}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-90" />
                  <span className="leading-tight">{planLabel(id)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body scroll */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-8 sm:py-6">
          <div className={`rounded-2xl border border-white/10 bg-white/[0.04] p-4 ring-1 ${styles.ring} sm:p-5`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex h-2 w-2 rounded-full ${styles.dot}`} aria-hidden />
              <h3 className="text-xl font-bold text-e0e3-foreground">{copy.title}</h3>
            </div>
            <p className="mt-2 text-sm font-medium text-e0e3-accent-soft">{copy.subtitle}</p>
            <p className="mt-4 text-sm font-semibold text-e0e3-foreground">{copy.intro}</p>

            <ul className="mt-3 space-y-2.5">
              {copy.benefits.map((line) => (
                <li key={line} className="flex gap-3 text-sm text-e0e3-foreground">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
                    <FaCheck className="h-3 w-3" />
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex gap-2 rounded-xl border border-e0e3-accent/25 bg-e0e3-accent/10 px-3 py-2.5 text-sm text-e0e3-foreground">
              <FaInfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-e0e3-accent-soft" />
              <span>{copy.highlight}</span>
            </div>
          </div>

          <h4 className="mt-6 text-sm font-bold text-e0e3-foreground">Compará los tres planes</h4>
          <p className="mt-1 text-xs text-e0e3-muted">
            Usuarios, sucursales y funciones clave de un vistazo.
          </p>
          <div className="mt-3">
            <PlanesComparisonTable highlightPlan={tab} />
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-5">
            <h4 className="text-sm font-bold text-e0e3-foreground">¿Necesitás más capacidad?</h4>
            <ul className="mt-3 space-y-2">
              <li className="flex items-center gap-2 text-sm text-e0e3-foreground">
                <FaUserPlus className="h-4 w-4 shrink-0 text-e0e3-accent-soft" />
                Usuarios extra
              </li>
              <li className="flex items-center gap-2 text-sm text-e0e3-foreground">
                <FaStoreAlt className="h-4 w-4 shrink-0 text-e0e3-accent-soft" />
                Sucursales extra
              </li>
            </ul>
            <p className="mt-3 text-sm text-e0e3-muted">
              Podés ampliar tu plan cuando tu negocio crezca.
            </p>
          </div>
        </div>

        <div className="shrink-0 border-t border-white/10 bg-e0e3-surface/95 px-5 py-4 sm:px-8">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-e0e3-accent px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-e0e3-accent/90 sm:w-auto sm:min-w-[140px]"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
